import { db } from '../config/firebase';
import { doc, runTransaction, getDoc } from 'firebase/firestore';

/**
 * Service to handle Reviews and Ratings logic
 */
const ReviewService = {
    /**
     * Submit a user review and atomically update global stats
     * @param {string} userId - The user's ID
     * @param {number} rating - Rating from 1 to 5
     * @returns {Promise<void>}
     */
    submitReview: async (userId, rating, comment = '') => {
        if (!userId) throw new Error("User ID is required");
        if (rating < 1 || rating > 5) throw new Error("Rating must be between 1 and 5");

        const userReviewRef = doc(db, 'reviews', userId);
        const statsRef = doc(db, 'marketing', 'stats');

        try {
            await runTransaction(db, async (transaction) => {
                // 1. Get current global stats
                const statsDoc = await transaction.get(statsRef);
                let currentCount = 0;
                let currentAverage = 0;

                if (statsDoc.exists()) {
                    const data = statsDoc.data();
                    currentCount = data.ratingCount || 0;
                    currentAverage = data.ratingValue || 0;
                }

                // 2. Check if user already reviewed
                const userReviewDoc = await transaction.get(userReviewRef);
                const existingReview = userReviewDoc.exists() ? userReviewDoc.data() : null;

                let newCount = currentCount;
                let newAverage = currentAverage;

                if (existingReview) {
                    // --- UPDATE EXISTING REVIEW ---
                    const oldRating = existingReview.rating;
                    if (currentCount > 0) {
                        const currentSum = currentAverage * currentCount;
                        const newSum = currentSum - oldRating + rating;
                        newAverage = newSum / currentCount;
                    } else {
                        // Should not happen if count logic is correct, but safe fallback
                        newAverage = rating;
                        newCount = 1;
                    }
                } else {
                    // --- NEW REVIEW ---
                    newCount = currentCount + 1;
                    const currentSum = currentAverage * currentCount;
                    newAverage = (currentSum + rating) / newCount;
                }

                // 3. Update Global Stats
                transaction.set(statsRef, {
                    ratingCount: newCount,
                    ratingValue: parseFloat(newAverage.toFixed(2)),
                    updatedAt: new Date()
                }, { merge: true });

                // 4. Save User Review
                transaction.set(userReviewRef, {
                    userId,
                    rating,
                    comment: comment.substring(0, 500), // Limit comment length
                    createdAt: existingReview ? existingReview.createdAt : new Date(),
                    updatedAt: new Date()
                });
            });

        } catch (error) {
            console.error("Error submitting review:", error);
            throw error;
        }
    },

    /**
     * Get user's existing review
     * @param {string} userId 
     */
    getUserReview: async (userId) => {
        if (!userId) return null;
        try {
            const docRef = doc(db, 'reviews', userId);
            const snap = await getDoc(docRef);
            return snap.exists() ? snap.data() : null;
        } catch (error) {
            console.error("Error getting user review:", error);
            return null;
        }
    },

    /**
     * Get the current global stats
     * @returns {Promise<{ratingValue: number, ratingCount: number} | null>}
     */
    getStats: async () => {
        try {
            const statsRef = doc(db, 'marketing', 'stats');
            const snap = await getDoc(statsRef);
            if (snap.exists()) {
                return snap.data();
            }
            return null;
        } catch (error) {
            console.error("Error fetching stats:", error);
            return null;
        }
    },

    /**
     * Check if a user has already reviewed
     * @param {string} userId 
     * @returns {Promise<boolean>}
     */
    hasUserReviewed: async (userId) => {
        if (!userId) return false;
        try {
            const userReviewRef = doc(db, 'reviews', userId);
            const snap = await getDoc(userReviewRef);
            return snap.exists();
        } catch (error) {
            console.error("Error checking user review:", error);
            return false;
        }
    }
};

export default ReviewService;
